import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// MANSA MONITOR — notifications feed. The same briefings that post to the Teams
// 'mansa-monitor' channel are shown here. Polls /strategies/api/mansa/feed/ every 20s.
// Windows: 07:00 PRE_WURA · 09:00 PRE_ANYANWU · 09:35 POST_ANYANWU · 16:05 ANYANWU_EXIT ·
// 15:45 PRE_OSUPA · 16:05 POST_OSUPA (NQ family) · 12:45 PRE_NUIT · 13:35 POST_NUIT ·
// 08:05 NUIT_EXIT (Gold). Each card renders an explanatory table built from the engine's
// ACTUAL decision rows; an AI briefing (DECISION/WHY/MEANS/WATCH) shows when available.

const REFRESH_MS = 20000;
const MANSA_COLOR = '#3b82f6';

const ES_SYS_COLOR = { SIRIUS: '#a855f7', RIGEL: '#2962ff', MARS: '#e11d48',
  ISINMI: '#f97316', SATURN: '#0ea5e9', VENUS: '#0d9488' };

const TYPE_COLOR = {
  PRE_ANYANWU: '#3b82f6', POST_ANYANWU: T.bull, ANYANWU_EXIT: '#f59e0b',
  PRE_OSUPA: '#eab308', POST_OSUPA: '#6366f1',
  PRE_NUIT: '#eab308', POST_NUIT: T.bull, NUIT_EXIT: '#f59e0b',
  PRE_ES: '#a855f7', POST_ES: '#a855f7',
  // ES desk — one briefing per system, fired at that system's own eval time.
  // ISINMI trades NQ (off the ES signal) → it lives under the NQ desk.
  ES_SIRIUS: ES_SYS_COLOR.SIRIUS, ES_RIGEL: ES_SYS_COLOR.RIGEL, ES_MARS: ES_SYS_COLOR.MARS,
  NQ_ISINMI: ES_SYS_COLOR.ISINMI, ES_SATURN: ES_SYS_COLOR.SATURN, ES_VENUS: ES_SYS_COLOR.VENUS,
};

// Asset tabs — each maps to the briefing window types for that desk.
// ISINMI is an ES-desk system by code, but it ENTERS NQ → it briefs under NQ.
const ASSET_WINDOWS = {
  NQ:   new Set(['PRE_ANYANWU', 'POST_ANYANWU', 'ANYANWU_EXIT', 'PRE_OSUPA', 'POST_OSUPA',
                 'NQ_ISINMI']),
  ES:   new Set(['PRE_ES', 'POST_ES', 'ES_SIRIUS', 'ES_RIGEL', 'ES_MARS', 'ES_SATURN', 'ES_VENUS']),
  GOLD: new Set(['PRE_NUIT', 'POST_NUIT', 'NUIT_EXIT']),
};
const ASSET_TABS = [['ALL', 'All'], ['NQ', 'NQ'], ['ES', 'ES'], ['GOLD', 'GC']];

// field -> [label, plain-language explanation]. Drives the explanatory table.
const FIELD_META = {
  hold_flag:      ['Hold gate', 'close > SMA100 and SMA rising'],
  hold_gate_proj: ['Hold gate (projected)', 'close > SMA100 and SMA rising'],
  pct_above_bp:   ['Price vs SMA100', 'bp above the 100-day SMA'],
  ext_pct:        ['Price vs SMA100', 'bp above the 100-day SMA'],
  sma_slope_bp:   ['SMA100 slope', 'bp · must be rising (> 0) to hold'],
  prior_ret_bp:   ['Prior-day return', 'bp, close-to-close'],
  mom5_bp:        ['5-day momentum', 'bp'],
  current_price:  ['Price (now)', 'live price at the pre-window — not the 16:00 close'],
  close:          ['Close', ''],
  close_at_eval:  ['Close at eval', '16:00 ET gate close'],
  sma100:         ['SMA100', '100-day simple moving average'],
  sma100_5ago:    ['SMA100 (5 bars ago)', 'used for the slope check'],
  entry_price:    ['Entry', ''],
  stop_px:        ['Stop', ''],
  stop_level:     ['Stop', ''],
  size:           ['Size', 'contracts'],
  on_ret:         ['Overnight return', 'bp'],
  on_ret_bp:      ['Overnight return', 'bp'],
  exit_price:     ['Exit', ''],
  pnl_bp:         ['P&L', 'bp'],
  stopped:        ['Stopped out', ''],
};
const FIELD_ORDER = ['hold_flag', 'hold_gate_proj', 'pct_above_bp', 'ext_pct', 'sma_slope_bp',
  'prior_ret_bp', 'mom5_bp', 'current_price', 'close', 'close_at_eval', 'sma100', 'sma100_5ago',
  'entry_price', 'stop_px', 'stop_level', 'size', 'on_ret', 'on_ret_bp', 'exit_price', 'pnl_bp', 'stopped'];
const SECTION_LABELS = ['DECISION', 'WHY', 'MEANS', 'WATCH'];

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso.replace(' ', 'T'));
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} ${time} ET`;
  } catch { return iso; }
}
const todayETStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

function fmtNum(v) {
  if (typeof v !== 'number') return String(v);
  return Number.isInteger(v) ? v.toLocaleString()
    : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtFieldVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return fmtNum(v);
  return String(v);
}
// Parse an AI briefing into its DECISION / WHY / MEANS / WATCH sections.
function parseSections(text) {
  if (!text) return null;
  const found = {};
  let cur = null;
  for (const line of String(text).split('\n')) {
    const m = line.match(/^\s*(DECISION|WHY|MEANS|WATCH)\s*[:\-]\s*(.*)$/i);
    if (m) { cur = m[1].toUpperCase(); found[cur] = m[2]; }
    else if (cur && line.trim()) { found[cur] += ' ' + line.trim(); }
  }
  return Object.keys(found).length ? found : null;
}
const decisionTone = (d) => {
  const s = String(d || '').toUpperCase();
  if (s.includes('TAKE') || s.includes('TRADE') || s === 'HOLD') return T.bull;
  if (s.includes('SKIP')) return T.gold;
  if (s.includes('EXIT')) return '#3b82f6';
  return T.textMuted;
};

function Chip({ label, value, color }) {
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, padding: '4px 11px',
      borderRadius: 6, background: `${color}1f`, border: `1px solid ${color}55` }}>
      <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: T.textMuted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color }}>{value}</span>
    </span>
  );
}

function Card({ n }) {
  const c = TYPE_COLOR[n.event_type] || MANSA_COLOR;
  const raw = n.mode === 'raw_state';
  const p = n.payload || {};
  const esDesk = p.es_desk;
  const esSystem = p.es_system;   // single-system ES briefing (ES_SIRIUS … ES_VENUS)
  // Merge the engine's actual decision row under the emitter event (event wins on conflict).
  const eng = p.nuit_decision || p.osupa_latest || {};
  const ev = { ...eng, ...(p.event || {}) };

  // Stale-locked guard (Director 2026-07-01): on a PRE_* projection, osupa_latest is the
  // LAST locked OSUPA decision (a prior session), not tonight's. Mark any field taken from
  // that locked row (ext_pct / entry_price / sma100_5ago) when its session_date predates
  // this briefing, so yesterday's bp / locked entry can't be misread as tonight's figure.
  const _locked = p.osupa_latest || null;
  const _lockedDate = _locked && _locked.session_date;
  const _lockedStale = !!_lockedDate && !!n.session_date && _lockedDate !== n.session_date;
  const fromLocked = (k) => _lockedStale && !!_locked && _locked[k] !== undefined
    && (!p.event || p.event[k] === undefined || p.event[k] === null);

  const sections = !raw ? parseSections(n.briefing) : null;
  // Panel-aware headline (Director 2026-06-30): osupa_action / osupa_reason / grade_tier
  // live in osupa_latest and are OSUPA's fields — they are CONTEXT on every OSUPA-family
  // card, NOT the panel's own decision. Only OSUPA panels should headline them; an ANYANWU
  // panel must show ANYANWU's decision (its LONG always fires on a HOLD-gate morning), not
  // OSUPA's SKIP/grade. This kills the "Decision: SKIP / Grade: A" contradiction.
  const et = n.event_type || '';
  const isOsupaPanel = et === 'PRE_OSUPA' || et === 'POST_OSUPA';
  const isAnyanwuPanel = et === 'PRE_ANYANWU' || et === 'POST_ANYANWU';
  let decision = ev.decision || ev.decision_proj || ev.action;
  if (!decision) {
    if (isOsupaPanel) decision = ev.osupa_action;
    else if (isAnyanwuPanel) decision = (eng.hold_flag || ev.hold_flag) ? 'TAKE' : 'SKIP';
  }
  // ANYANWU grades off OSUPA's overnight — if OSUPA is stale (no live overnight, e.g.
  // the morning after a holiday), the engine returns missing_grade_inputs → SKIP. Don't
  // carry a stale HOLD forward as a projected TAKE; project the SKIP the engine will make.
  if (isAnyanwuPanel && _lockedStale) decision = 'SKIP';
  // tier chip: OSUPA panels show OSUPA's tier; ANYANWU panels show the projected tier only.
  let tier = ev.tier || ev.tier_proj;
  if (!tier && isOsupaPanel) tier = ev.grade_tier;
  // OSUPA's tier is A/B/C; never badge a C (skip) chip next to SKIP.
  if (tier === 'C') {
    if (!decision || decision === 'HOLD') decision = 'SKIP';
  }
  const showTier = tier && tier !== 'C';
  const note = ev.note;
  const reason = (isAnyanwuPanel && _lockedStale)
    ? `missing OSUPA overnight — OSUPA last ran ${_lockedDate} (no live overnight to grade)`
    : (ev.reason || ev.osupa_reason || ev.exit_reason || (esSystem && esSystem.reason));
  // NYSE non-session (holiday / early-close half-day): the desk doesn't trade, so a
  // SKIP next to a leftover Grade is expected — flag it on the header so it reads as
  // "skipped for holiday", not a contradiction.
  const isHoliday = !!reason && String(reason).toLowerCase().includes('non_session');
  const rows = FIELD_ORDER.filter(k => ev[k] !== undefined && ev[k] !== null && ev[k] !== '');
  const hasStructured = !!sections || rows.length > 0 || decision || tier || !!esDesk || !!esSystem;

  return (
    <div style={{ border: `1px solid ${T.border}`, borderLeft: `3px solid ${c}`, borderRadius: 8,
      background: T.surface, padding: '12px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15 }}>{n.emoji}</span>
        <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{n.title}</span>
        <span style={{ fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
          {n.event_type}</span>
        {isHoliday && (
          <span
            title="NYSE non-session (holiday / early-close half-day) — the desk does not trade today, so any grade/size shown is moot."
            style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#f0b90b', background: 'rgba(240,185,11,0.14)',
              border: '1px solid rgba(240,185,11,0.42)', borderRadius: 4, padding: '2px 8px',
            }}>
            🗓 Skipped because of holiday
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted,
          fontFamily: 'Roboto Mono, monospace' }}>{fmtET(n.received_at)}</span>
      </div>

      {(decision || showTier) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {decision && <Chip label="Decision" value={String(decision)} color={decisionTone(decision)} />}
          {showTier && <Chip label="Grade" value={String(tier)} color={c} />}
        </div>
      )}

      {sections && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px' }}>
          {SECTION_LABELS.filter(k => sections[k]).map(k => (
            <React.Fragment key={k}>
              <b style={{ color: c, fontSize: 11, letterSpacing: '0.06em' }}>{k}</b>
              <span style={{ color: T.textDim, fontSize: 12.5, lineHeight: 1.5 }}>{sections[k]}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <table style={{ marginTop: 12, width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {rows.map(k => {
              const meta = FIELD_META[k] || [k, ''];
              const stale = fromLocked(k);
              const label = stale ? `${meta[0]} (locked ${_lockedDate})` : meta[0];
              const hint = stale
                ? `OSUPA's last locked decision (${_lockedDate}) - not tonight's live figure`
                : meta[1];
              return (
                <tr key={k} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ padding: '5px 12px 5px 0', verticalAlign: 'top' }}>
                    <div style={{ color: T.text, fontWeight: 600 }}>{label}</div>
                    {hint && <div style={{ color: T.textMuted, fontSize: 10.5, marginTop: 1 }}>{hint}</div>}
                  </td>
                  <td style={{ padding: '5px 0', textAlign: 'right', color: T.textDim, verticalAlign: 'top',
                    fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>{fmtFieldVal(ev[k])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {esDesk && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
            ES desk · {esDesk.es_candles_today ?? 0} ES 1-min bars today
            {esDesk.features
              ? ` · sirius_ret1 ${fmtFieldVal(esDesk.features.sirius_ret1)} · rigel_ret1 ${fmtFieldVal(esDesk.features.rigel_ret1)} · slope ${fmtFieldVal(esDesk.features.slope_lag1)}`
              : ' · daily features pending (≈09:20 ET)'}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              {Object.entries(esDesk.systems || {}).map(([name, s]) => {
                const dec = (s && s.decision) || '—';
                const awaiting = dec === '—' || dec === '';
                const sc = ES_SYS_COLOR[name] || MANSA_COLOR;
                return (
                  <tr key={name} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ padding: '5px 12px 5px 0', fontWeight: 700, color: sc }}>{name}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                      <span style={{ color: awaiting ? T.textMuted : decisionTone(dec), fontWeight: 600 }}>
                        {awaiting ? 'awaiting' : dec}{!awaiting && s.side && s.side !== '—' ? ` ${s.side}` : ''}</span>
                    </td>
                    <td style={{ padding: '5px 0', textAlign: 'right', color: T.textMuted,
                      fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>
                      {s && s.grade && s.grade !== '—' ? `grade ${s.grade}` : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {esSystem && (
        <div style={{ marginTop: 10 }}>
          {esSystem.desc && (
            <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 8 }}>{esSystem.desc}</div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chip label="Decision" value={String(esSystem.decision || '—')} color={decisionTone(esSystem.decision)} />
            {esSystem.direction && esSystem.direction !== '—' && (
              <Chip label="Direction" value={String(esSystem.direction)}
                color={esSystem.direction === 'LONG' ? T.bull : esSystem.direction === 'SHORT' ? T.bear : c} />
            )}
            {esSystem.grade && esSystem.grade !== '—' && <Chip label="Grade" value={String(esSystem.grade)} color={c} />}
            {esSystem.mult != null && <Chip label="Size" value={`${esSystem.mult}×`} color={c} />}
            {esSystem.contracts != null && (
              <Chip label="Contract" value={`${esSystem.contracts}c`}
                color={esSystem.contracts > 0 ? T.bull : T.textMuted} />
            )}
          </div>
          {Array.isArray(esSystem.upstream) && esSystem.upstream.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 10.5, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
                reads:</span>
              {esSystem.upstream.map(u => {
                const good = u.ok === true;               // good FOR THIS system, per relationship
                const verb = u.took
                  ? `took${u.side && u.side !== '—' ? ' ' + u.side : ''}`
                  : (u.decision === 'SKIP' ? 'skipped' : (u.decision || '—'));
                const relWord = u.rel === 'mirror' ? 'mirrors' : 'yields to';
                const meaning = u.ok == null ? 'pending'
                  : good ? (u.rel === 'mirror' ? 'signal present' : 'slot free → may trade')
                         : (u.rel === 'mirror' ? 'no signal' : 'leader holds slot → blocked');
                return (
                  <span key={u.name}
                    title={`${relWord} ${u.name} — ${u.name} ${verb}${u.at ? ` ${u.at} ET` : ''} · ${meaning}`}
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', padding: '2px 7px',
                      borderRadius: 4, fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap',
                      color: good ? T.bull : T.textMuted,
                      background: good ? 'rgba(38,166,154,0.12)' : 'rgba(128,128,128,0.10)',
                      border: `1px solid ${good ? 'rgba(38,166,154,0.42)' : T.border}`,
                    }}>
                    <span style={{ opacity: 0.55 }}>{u.rel === 'mirror' ? 'mirror' : 'yield'}</span>{' '}
                    {u.name} {verb}{u.at ? ` ${u.at}` : ''}{good ? ' ✓' : ''}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
            {esSystem.features
              ? `daily features · sirius_ret1 ${fmtFieldVal(esSystem.features.sirius_ret1)} · rigel_ret1 ${fmtFieldVal(esSystem.features.rigel_ret1)} · slope ${fmtFieldVal(esSystem.features.slope_lag1)}`
              : 'daily features pending (≈09:20 ET)'}
            {` · ${esSystem.es_candles_today ?? 0} ES 1-min bars`}
            {(esSystem.entry != null || esSystem.stop != null)
              ? ` · entry ${fmtFieldVal(esSystem.entry)} · stop ${fmtFieldVal(esSystem.stop)}` : ''}
          </div>
        </div>
      )}

      {(note || reason) && (
        <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted, fontStyle: 'italic', lineHeight: 1.5 }}>
          {note || reason}</div>
      )}

      {!hasStructured && (
        <pre style={{ margin: '10px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'Roboto Mono, monospace', fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>
          {n.briefing || '(awaiting briefing…)'}</pre>
      )}

      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 10.5, color: T.textMuted, letterSpacing: '0.04em' }}>
          Raw payload</summary>
        <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'Roboto Mono, monospace', fontSize: 11, color: T.textMuted, lineHeight: 1.45 }}>
          {n.briefing || JSON.stringify(n.payload, null, 2)}</pre>
      </details>

      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          padding: '2px 7px', borderRadius: 4, color: raw ? T.gold : T.bull,
          background: `${raw ? T.gold : T.bull}1f`, border: `1px solid ${(raw ? T.gold : T.bull)}55` }}>
          {raw ? 'engine data' : 'AI briefing'}</span>
        <span style={{ fontSize: 10, color: T.textMuted, fontFamily: 'Roboto Mono, monospace' }}>
          session {n.session_date}</span>
      </div>
    </div>
  );
}

export default function MansaMonitor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [assetTab, setAssetTab] = useState('ALL');
  const [limit, setLimit] = useState(100);
  const timerRef = useRef(null);

  const fetchFeed = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = `${API_BASE}/strategies/api/mansa/feed/?limit=${limit}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setItems(j.notifications || []);
      setLastFetched(new Date());
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [limit]);

  useEffect(() => {
    fetchFeed();
    timerRef.current = setInterval(fetchFeed, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchFeed]);

  // Asset tab filtering is client-side (a tab spans several window types per desk).
  const displayed = assetTab === 'ALL'
    ? items
    : items.filter(n => (ASSET_WINDOWS[assetTab] || new Set()).has(n.event_type));
  const todayCount = displayed.filter(n => n.session_date === todayETStr()).length;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
        paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>Briefings</h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          AI briefings for the OṢUPA / ANYANWỤ family + the ES desk (NQ &amp; ES) and NUIT (Gold) ·
          also delivered to the Teams <b>mansa-monitor</b> channel
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
            style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={50}>50</option><option value={100}>100</option><option value={200}>200</option>
          </select>
          <button onClick={fetchFeed} disabled={loading} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase', borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
            border: 'none', background: `${MANSA_COLOR}2e`, color: MANSA_COLOR }}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
        {ASSET_TABS.map(([key, label]) => {
          const active = assetTab === key;
          const n = key === 'ALL' ? items.length
            : items.filter(it => (ASSET_WINDOWS[key] || new Set()).has(it.event_type)).length;
          return (
            <button key={key} onClick={() => setAssetTab(key)}
              style={{ padding: '7px 22px', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                borderRadius: 7, cursor: 'pointer', transition: 'all 0.12s',
                border: `1px solid ${active ? MANSA_COLOR : T.border}`,
                background: active ? `${MANSA_COLOR}22` : T.surface,
                color: active ? MANSA_COLOR : T.textDim }}>
              {label}
              <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 600, opacity: 0.7,
                fontFamily: 'Roboto Mono, monospace' }}>{n}</span>
            </button>
          );
        })}
      </div>

      <details style={{ marginBottom: 14, border: `1px solid ${T.border}`, borderRadius: 8,
        background: T.surface, padding: '10px 14px' }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.text,
          letterSpacing: '0.02em' }}>How to read a briefing</summary>
        <div style={{ marginTop: 10, fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
          Each card leads with the headline <b>Decision · Grade</b>, then an explanatory table of
          the gate inputs the engine actually used (each row names the feature and what it means). When an
          AI briefing is available it adds four lines:
          <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            <b style={{ color: T.text }}>DECISION</b><span>exactly what the system is doing (HOLD / SKIP / TAKE, tier, size, direction).</span>
            <b style={{ color: T.text }}>WHY</b><span>the specific gate or grade that drove it — the feature value vs its cutoff.</span>
            <b style={{ color: T.text }}>MEANS</b><span>what happens next — the active stop, and what tomorrow's linked book will do.</span>
            <b style={{ color: T.text }}>WATCH</b><span>the one thing that could change the outcome, or the key risk to monitor.</span>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted }}>
            Windows — <b>NQ family:</b> 09:00 / 09:35 / 16:05 ANYANWU · 15:45 / 16:05 OSUPA.
            <b> ES desk:</b> SIRIUS/RIGEL/MARS/ISINMI (RTH) · SATURN/VENUS (overnight).
            <b> Gold:</b> 12:45 / 13:35 NUIT · 08:05 NUIT exit. NUIT reports the live <b>base</b> book
            (flat 1 contract, −50bp stop); DXY/VIX sizing is not live.
          </div>
        </div>
      </details>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: 'rgba(239,68,68,0.1)',
          border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>Error: {error}</div>
      )}

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10, fontFamily: 'Roboto Mono, monospace' }}>
        {displayed.length} notification{displayed.length === 1 ? '' : 's'}
        {assetTab !== 'ALL' && ` · ${assetTab === 'GOLD' ? 'GC' : assetTab} only`} · {todayCount} today · newest first
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET · auto-refresh ${REFRESH_MS / 1000}s`}
      </div>

      {displayed.length === 0 && !loading && (
        <div style={{ padding: '24px 14px', color: T.textMuted, textAlign: 'center', fontStyle: 'italic',
          border: `1px dashed ${T.border}`, borderRadius: 8 }}>
          No briefings yet. The emitters post at their windows (NQ: 09:00 / 09:35 / 15:45 / 16:05 ET ·
          ES desk: ~09:24 / 16:05 ET · Gold: 12:45 / 13:35 / 08:05 ET) once the TradingView alerts are
          firing — they appear here and in the Teams channel.
        </div>
      )}
      {displayed.map(n => <Card key={n.id} n={n} />)}
    </div>
    </div>
  );
}
